import {
  type DefaultCoverOffReason,
  type DefaultSuperInsuranceInput,
  DefaultSuperInsuranceInputSchema,
  type DefaultSuperInsuranceOutput,
  type DefaultSuperInsuranceRuleSet,
} from "./types.js";

// ---------------------------------------------------------------------------
// HARD RULE (binding, panel §2 + figure ledger #6): default life / TPD /
// income-protection cover inside super IS a Corporations Act s764A financial
// product with NO s765A carve-out (that carve-out is health-insurance-specific).
// So this surface must emit NO personalised dollar/premium/coverage number and
// NO super-fund / super-product / SMSF / USI / SPIN identifier or
// keep/cancel/buy/switch handoff — affiliate revenue is $0 by design.
//
// We encode BOTH halves as a guard run over every emitted output object, not as
// doc prose:
//   1. assertNoProductHandoff — walks every string value and throws if a
//      fund/product identifier or a keep/cancel/buy/switch CTA leaks in.
//   2. assertNoPersonalisedDollar — walks every NUMBER value and throws if a
//      dollar-magnitude figure (anything > a small count) leaks in. The output
//      is rule-booleans + tiny counts only; a premium/coverage/dollar number
//      tripping in becomes a loud test failure rather than a silent AFSL breach.
// ---------------------------------------------------------------------------

/** Tokens that signal a super-product / fund identifier or a handoff CTA. */
const PRODUCT_HANDOFF_TOKENS = [
  "fund",
  "smsf",
  "industry super",
  "retail super",
  "open an account",
  "sign up",
  "join now",
  "switch to",
  "switch now",
  "cancel",
  "keep your cover",
  "buy cover",
  "rollover to",
  "consolidate with",
  "affiliate",
  "sponsored",
  "partner offer",
  "compare",
  "apply now",
  "best super",
  "usi", // unique superannuation identifier
  "abn:",
  "spin:", // superannuation product identification number
];

/**
 * Guard #1: assert no fund/super-product identifier or keep/cancel/buy/switch
 * handoff can reach the output. Walks every string value and throws on a match.
 * Pure; returns the output unchanged when clean so it can wrap a `return`.
 */
export function assertNoProductHandoff<T>(output: T): T {
  const visit = (value: unknown): void => {
    if (typeof value === "string") {
      const haystack = value.toLowerCase();
      for (const token of PRODUCT_HANDOFF_TOKENS) {
        if (haystack.includes(token)) {
          throw new Error(
            `default-super-insurance: blocked super-product/handoff in an awareness result (matched "${token}"). Default cover inside super is an s764A financial product — no product identifier or keep/cancel/buy/switch handoff may reach this surface (panel §2).`,
          );
        }
      }
      return;
    }
    if (Array.isArray(value)) {
      for (const item of value) visit(item);
      return;
    }
    if (value && typeof value === "object") {
      for (const v of Object.values(value)) visit(v);
    }
  };
  visit(output);
  return output;
}

/**
 * The largest numeric value the awareness output may legitimately carry. The
 * output is rule-booleans plus tiny counts (e.g. an account count); it must
 * NEVER carry a personalised dollar/premium/coverage figure. Anything above
 * this is, by construction, a leaked dollar number → AFSL breach.
 */
const MAX_NON_DOLLAR_VALUE = 100;

/**
 * Guard #2: assert NO personalised dollar/premium/coverage number can reach the
 * output. Walks every number value and throws if any exceeds the small-count
 * ceiling. Pure; returns the output unchanged when clean.
 */
export function assertNoPersonalisedDollar<T>(output: T): T {
  const visit = (value: unknown): void => {
    if (typeof value === "number") {
      if (Math.abs(value) > MAX_NON_DOLLAR_VALUE) {
        throw new Error(
          `default-super-insurance: blocked a personalised dollar/coverage number (${value}) in an awareness result. This tool emits NO personalised dollar figure by design — it explains which rules apply, never a number for THIS account (panel §2, ledger #6).`,
        );
      }
      return;
    }
    if (typeof value === "string") return;
    if (Array.isArray(value)) {
      for (const item of value) visit(item);
      return;
    }
    if (value && typeof value === "object") {
      for (const v of Object.values(value)) visit(v);
    }
  };
  visit(output);
  return output;
}

/** Run both hard-rule guards over an output object before it is returned. */
function guardOutput<T>(output: T): T {
  return assertNoPersonalisedDollar(assertNoProductHandoff(output));
}

/**
 * Run the default-super-insurance AWARENESS check for one assessment. Pure:
 * same inputs + ruleset always yield the same output. Validates at the boundary
 * so malformed payloads fail loudly. Emits ONLY which statutory rules apply to
 * an account like the one described, plus the duplicate-premium caution — never
 * a dollar number, never a product handoff, never a keep/cancel/buy/switch
 * steer.
 */
export function checkDefaultSuperInsurance(
  rawInput: DefaultSuperInsuranceInput,
  rules: DefaultSuperInsuranceRuleSet,
): DefaultSuperInsuranceOutput {
  const input = DefaultSuperInsuranceInputSchema.parse(rawInput);

  // PMIF age-25 trigger: default (opt-out) cover only from age 25.
  const under25 = input.age < rules.minAgeForDefaultCover;

  // PMIF $6,000 trigger: tested against the coarse balance band. Only the
  // "under6k" band is below the threshold; "unknown" doesn't assert the trigger.
  const balanceUnder6k = input.balanceBand === "under6k";

  // PYS 16-month inactivity trigger. Null when inactivity wasn't supplied —
  // the rule is explained by copy but not asserted as firing.
  const inactive16Months =
    input.inactiveMonths === null
      ? null
      : input.inactiveMonths >= rules.inactiveMonthsCancelThreshold;

  const reasons: DefaultCoverOffReason[] = [];
  if (under25) reasons.push("under25");
  if (balanceUnder6k) reasons.push("balanceUnder6k");
  if (inactive16Months === true) reasons.push("inactive16Months");

  const defaultCoverLikelyOff = reasons.length > 0;

  // Default cover GENERALLY applies to an account like this when no opt-out
  // trigger fires AND we actually know the inactivity position (so we're not
  // silently assuming an unsupplied input is clear).
  const defaultCoverGenerallyApplies =
    !defaultCoverLikelyOff && inactive16Months !== null;

  const multipleAccounts = input.activeAccountCount >= 2;

  return guardOutput({
    under25,
    balanceUnder6k,
    inactive16Months,
    defaultCoverLikelyOff,
    reasons,
    multipleAccounts,
    defaultCoverGenerallyApplies,
  });
}
